import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import 'rc-slider/assets/index.css';
import { useGetQuestionsMutation } from '../../../../../features/authentication/qSetApiSlice';
import PreviewHeader from '../../../../assignments/pages/MoreActions/components/PreviewHeader';
import QuestionPrintContainer from '../components/QuestionPrintContainer';
import PrintOptions from '../../../../assignments/pages/MoreActions/components/PrintOptions';
import { useGetPrintOptionsMutation, useUpdatePrintOptionsMutation } from '../../../../../features/authentication/persistApiSlice';
import { DeStackSetData, ResetSettings, StackSetData } from '../../../utils/displaySetFunctions';

const DisplayQuestionSet = () => {
  const location = useLocation();
  const { setId } = useParams();
  const { setName, languageId } = location.state;
  const [questions, setQuestions] = useState({});
  const [questionsData, setQuestionsData] = useState([]);
  const [fontStyleClass, setFontStyleClass] = useState('times');
  const [displayTopValue, setDisplayTopValue] = useState('');
  const [printOptions, setPrintOptions] = useState({});
  const [originalSettingsData, setOriginalSettingsData] = useState({});
  const [contentHeights, setContentHeights] = useState([]);
  const [hideDuplicate, setHideDuplicate] = useState(false);
  const [imageCompressionValue, setImageCompressionValue] = useState(50);
  const standardPageHeight = 1300;

  const [getPrintOptions] = useGetPrintOptionsMutation();
  const [updatePrintOptions] = useUpdatePrintOptionsMutation();
  const [qSetQuestions, {isLoading}] = useGetQuestionsMutation();

  const getPrintOptionsAsync = async () => {
    const printOptionsResponse = await getPrintOptions().unwrap();

    let options = {
      ...printOptionsResponse, 
      imageCompressionPercent: printOptionsResponse?.imageCompressionPercent > 0 ? printOptionsResponse.imageCompressionPercent : 50
    };

    setOriginalSettingsData(options);
    setPrintOptions({ ...printOptions, ...options });
    if (printOptions?.imageCompressionPercent == 0) updateSettings({ imageCompressionPercent: options.imageCompressionPercent });
  };

  const updatePrintOptionsAsync = async (options) => {
    try {
      await updatePrintOptions(options).unwrap();
    } catch (ex) {
      console.log(ex);
    }
  };

  const getSetQuestionsAsync = async () => {
    const options = {
      id: setId,
      languageId: languageId
    };
    try {
      const response = await qSetQuestions(options);
      if(response.data) {
        setQuestions(response.data);
        let updatedQuestionInfo = response.data.questions?.map((item, index) => {
          return { ...item, questionNo: index + 1, isChecked: true};
        });
        setQuestionsData(updatedQuestionInfo);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleQuestionsSelect = (event) => {
    const { name, checked } = event.target;
    if (name === 'selectAll') {
      const data = questionsData?.map((item) => {
        return { ...item, isChecked: checked };
      });
      setQuestionsData(data);
    } else {
      let updatedQuestionsData = questionsData?.map((item) =>
        `question${item.questionId}` === name ? { ...item, isChecked: checked } : item
      );
      setQuestionsData(updatedQuestionsData);
    }
  };

  const handleClearAll = () => {
    const data = questionsData?.map((item) => {
      return { ...item, isChecked: false };
    });
    setQuestionsData(data);
  };

  useEffect(() => {
    getPrintOptionsAsync();
    getSetQuestionsAsync();
  }, []);

  useEffect(() => {
    if (contentHeights.length > 0) {
      setHideDuplicate(true);
    }
  }, [contentHeights]);

  const getContentHeight = (id) => {
    const item = contentHeights.find(q => q.id === id);
    return item ? item.height : 0;
  };

  const updateSettings = (data) => {
    setPrintOptions({ ...printOptions, ...data });
    updatePrintOptionsAsync({ ...printOptions, ...data });
  };

  const resetSettings = () => {
    let updatedSettings = ResetSettings(originalSettingsData);
    setPrintOptions(updatedSettings);
    updateSettings(updatedSettings);
  };

  useEffect(() => {
    if (!questionsData || questionsData?.length === 0 || !contentHeights) return;

    if (printOptions.twoColumn) {
      const updatedData = StackSetData(questionsData, { getContentHeight, standardPageHeight });
      setQuestionsData(updatedData);
    } else {
      const updatedData = DeStackSetData(questionsData);
      setQuestionsData(updatedData);
    }
  }, [printOptions?.twoColumn, questionsData]);

  return (
    <div className="vocabulary-container">
      <div className="vocabulary-header row">
        <div className="col-lg-9">
          <PreviewHeader
            printLabel={'Display Set'}
            isLoading={isLoading}
          />
          <div className="print-content-scroll">
            <QuestionPrintContainer
              setName={setName}
              displayTopValue={displayTopValue}
              fontStyleClass={fontStyleClass}
              printOptions={printOptions}
              isLoading={isLoading}
              teacherName={questions?.teacherName}
              questionsData={questionsData}
              handleQuestionsSelect={handleQuestionsSelect}
              handleClearAll={handleClearAll}
              setContentHeights={setContentHeights}
              hideDuplicate={hideDuplicate}
            />
          </div>
        </div>
        <div className="col-lg-3">
          <PrintOptions
            setName={setName}
            displayTopValue={displayTopValue}
            setDisplayTopValue={setDisplayTopValue}
            setFontStyleClass={setFontStyleClass}
            imageCompressionValue={imageCompressionValue}
            setImageCompressionValue={setImageCompressionValue}
            printOptions={printOptions}
            isLoading={isLoading}
            updateSettings={updateSettings}
            resetSettings={resetSettings}
          />
        </div>
      </div>
    </div>
  );
};

export default DisplayQuestionSet;
